<script setup lang="ts">
import ArrowForward from "../icons/ArrowForward.vue";
import ArrowBackward from "../icons/ArrowBackward.vue";
import MyTravel from "@/components/icons/MyTravel.vue";
import {
  CARRIER_BOOKED_ORDER,
  LOADED_AT_SENDER,
  DIRECTION_DELIVERY,
  DIRECTION_PICKUP,
  ARRIVED_BY_ROAD,
  licensePlateRegex,
  CARRIER_BOOKED_PICKUP,
  COUNTER_CANCELLATION_NEEDED,
} from "@/utils/constants";
import { ref } from "vue";
import DoneDetails from "./DoneDetails.vue";
import { i18n } from "@/i18n/i18n";
import store from "@/store";
import router from "@/router";
import StatusChangeButton from "@/components/StatusChangeButton.vue";
import ElectricLocomotive from "@/components/icons/ElectricLocomotive.vue";

const props = defineProps({
  order: {
    type: Object,
    default: () => ({
      orderId: null,
      orderPosition: null,
      primaryReference: null,
      secondaryReference: null,
      directionType: null,
      transportationState: null,
      loading_unit_formatted: null,
      loadUnit: {
        identifierPrefix: null,
        identifierNumber: null,
        type: null,
        lengthCode: null,
        heightCode: null,
        emptyWeight: null,
        loadWeight: null,
        totalWeight: null,
        loadingStatus: null,
        dangerousGoods: false, // Set default value for boolean properties
        waste: false,
        limitedQuantity: false,
        customsGoods: false,
      },
      train: {
        trainNumber: null,
        operatorId: null,
        operatorName: null,
        shippingDate: null,
        trainClosingTime: null,
        trainArrivalTime: null,
        trainState: null,
      },
      terminal: {
        terminalId: {
          locationCode: null,
          authority: null,
        },
        terminalName: null,
      },
      freightForwarderId: null,
      freightForwarderName: null,
      licensePlate: null,
      driverId: null,
    }),
  },
  index: Number,
  activeOrder: Number || null,
});

const emit = defineEmits(["activeOrder"]);
const isEditing = ref(props.activeOrder == -1);
const showCounterCancellationNeeded = ref(false);

const cancelPermission = () => {
  showCounterCancellationNeeded.value = true;

  if (
    props.order.transportationStatus == CARRIER_BOOKED_PICKUP &&
    props.order?.directionType?.toLowerCase() == DIRECTION_PICKUP
  ) {
    showCounterCancellationNeeded.value = true;
  } else {
    cancel();
    toggleOrder();
    router.push("/");
  }
};

const cancel = () => {
  store.dispatch("OrderStore/removeTour", {
    order: props.order,
    index: props.index,
  });
};

const cancelPickUpOrderCamcellationNeeded = () => {
  let newTransportationStatus = "";
  switch (props.order.transportationStatus) {
    case CARRIER_BOOKED_PICKUP:
      newTransportationStatus = COUNTER_CANCELLATION_NEEDED;
      break;
    default:
      break;
  }

  if (newTransportationStatus) {
    store.dispatch("OrderStore/updateTourOrder", {
      order: props.order,
      index: props.index,
      params: {
        transportationStatus: newTransportationStatus,
      },
    });
  } else {
    console.error(
      "We don't know how to handle this transportationStatus yet. Please update code."
    );
  }

  showCounterCancellationNeeded.value = false;
  toggleOrder();
};

const toggleOrder = () => {
  if (props.activeOrder == null) {
    router.push(
      `/done/${props.order.orderId}/${props.order.orderPosition}/${props.order.directionType}`
    );
  } else {
    router.push("/done");
  }
};

const isNotProduction = () => {
  return process.env.NODE_ENV !== "production";
};

const formatKg = (value: string | number) => {
  if (!value) return;
  value = parseFloat(value as string);
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " kg";
};

const licensePlateInvalid = () => {
  if (!props.order.licensePlate) return true;
  return !licensePlateRegex.test(props.order.licensePlate);
};

const setButtonString = () => {
  if (props.order.transportationStatus === CARRIER_BOOKED_ORDER) {
    return i18n.global.t("tour.taken_over_at_sender");
  } else if (props.order.transportationStatus === LOADED_AT_SENDER) {
    return i18n.global.t("tour.arrival_terminal");
  } else if (props.order.transportationStatus === ARRIVED_BY_ROAD) {
    return i18n.global.t("tour.delivered_to_terminal");
  } else {
    return i18n.global.t("tour.delivered_to_terminal"); // TODO placeholder
  }
};
</script>
<template>
  <div
    v-if="activeOrder == null || activeOrder == index"
    class="bg-white w-full rounded-lg shadow-xl"
    :order-id="order.orderId"
  >
    <div
      @click="toggleOrder"
      class="grid grid-cols-3 mx-2 border-b pt-2 opacity-50"
    >
      <div class="col-span-2 flex">
        <div class="w-20 h-14 mt-2">
          <ArrowForward
            v-if="order?.directionType?.toLowerCase() == DIRECTION_DELIVERY"
          ></ArrowForward>
          <ArrowBackward v-else></ArrowBackward>
        </div>
        <div class="flex flex-col justify-start pt-2 text-left">
          <span
            v-if="order?.directionType?.toLowerCase() == DIRECTION_DELIVERY"
            class="font-bold text-md"
            >{{ $t("order.delivery") }}</span
          >
          <span v-else class="font-bold text-md">{{
            $t("order.pick_up")
          }}</span>
          <span class="font-normal text-sm">{{
            order.terminal.terminalName
          }}</span>
          <span v-if="isNotProduction()" class="font-normal text-sm"
            >Status: {{ order.transportationStatus }}</span
          >
        </div>
      </div>

      <div class="flex flex-col pt-2 text-left break-all">
        <span class="font-normal text-sm"
          >{{ $t("order.ref") }}: {{ order.primaryReference }}</span
        >
        <span class="font-normal text-sm"
          >{{ $t("order.origRef") }}: {{ order.secondaryReference }}</span
        >
        <span v-if="activeOrder == null" class="font-normal text-sm">
          {{ order?.loadUnit?.identifierPrefix
          }}{{ order?.loadUnit?.identifierNumber }}</span
        >
        <span
          v-if="activeOrder == null"
          class="font-normal d-flex flex-col text-sm"
          ><div class="font-normal d-flex gap-1 text-sm">
            {{ $t("order.loadWeightShort") }}:
            {{ formatKg(order?.loadUnit?.loadWeight) }}
          </div>
          <div class="font-normal d-flex gap-1 text-sm">
            {{ $t("order.emptyWeightShort") }}:
            {{ formatKg(order?.loadUnit?.emptyWeight) }}
          </div></span
        >
      </div>
      <div
        class="col-span-3"
        v-if="
          order.loadUnit.customsGoods &&
          (order.loadUnit.dangerousGoods ||
            order.loadUnit.waste ||
            order.loadUnit.limitedQuantity)
        "
      >
        <span class="text-red-600 flex items-center justify-center">
          <db-icon icon="info" variant="24-outline"></db-icon>
          <span v-if="order.loadUnit.dangerousGoods">{{
            $t("order.error_customs_with_dangerous", {
              type: $t("order.dangerous_goods"),
            })
          }}</span>
          <span v-if="order.loadUnit.waste">{{
            $t("order.error_customs_with_dangerous", {
              type: $t("order.waste"),
            })
          }}</span>
          <span v-if="order.loadUnit.limitedQuantity">{{
            $t("order.error_customs_with_dangerous", {
              type: $t("order.limitedQuantity"),
            })
          }}</span>
        </span>
      </div>
      <div class="col-span-3" v-else-if="order.loadUnit.customsGoods">
        <span class="text-red-600 flex items-center justify-center">
          <db-icon icon="info" variant="24-outline"></db-icon>
          <span>{{ $t("order.error_customs") }}</span>
        </span>
      </div>
      <div
        class="col-span-3"
        v-else-if="
          order.loadUnit.dangerousGoods ||
          order.loadUnit.waste ||
          order.loadUnit.limitedQuantity
        "
      >
        <span class="text-red-600 flex items-center justify-center">
          <db-icon icon="info" variant="24-outline"></db-icon>
          <span v-if="order.loadUnit.dangerousGoods">{{
            $t("order.error_goods", { type: $t("order.dangerous_goods") })
          }}</span>
          <span v-if="order.loadUnit.waste">{{
            $t("order.error_goods", { type: $t("order.waste") })
          }}</span>
          <span v-if="order.loadUnit.limitedQuantity">{{
            $t("order.error_goods", { type: $t("order.limitedQuantity") })
          }}</span>
        </span>
      </div>
    </div>
    <DoneDetails
      v-if="activeOrder != null"
      :readonly="true"
      :order="order"
    ></DoneDetails>
    <div
      v-else-if="
        activeOrder == null &&
        order.transportationStatus !== COUNTER_CANCELLATION_NEEDED
      "
      class="flex justify-between p-2"
    >
      <div class="text-slate-600">
        <ElectricLocomotive disabled="true"></ElectricLocomotive
        >{{ order.train.trainNumber }}
      </div>
      <StatusChangeButton
        :order="order"
        :index="index"
        classList="hydrated mt-2 px-2"
        variant="primary"
        :disabled="true"
      ></StatusChangeButton>
    </div>
    <div v-if="order.transportationStatus === COUNTER_CANCELLATION_NEEDED">
      <div class="flex pt-4 pb-2 pl-3">
        <MyTravel color="red"></MyTravel>
        <p class="text-red-500 pr-2">
          {{ $t("tour.counter_cancellation_needed") }}
        </p>
      </div>
    </div>
  </div>
</template>
